import React, { useEffect, useMemo, useState } from 'react'
import { api } from '../lib/api'

type Stats = {
  totals: { media: number; embeddings: number }
  by_status: Record<string, number>
  by_course: Record<string, number>
  recent: { id: number; title: string; status: string; course_id?: string | null; course_title?: string | null; created_at: string }[]
  activity_7d: { day: string; count: number }[]
}

const statusPalette: Record<string, string> = {
  uploaded: 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-200',
  transcribed: 'bg-sky-100 text-sky-700 dark:bg-sky-900/40 dark:text-sky-200',
  indexed: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-200',
  error: 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-200',
}

export default function DashboardPage() {
  const [stats, setStats] = useState<Stats | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get('/stats')
        setStats(data)
      } catch (e: any) {
        setError(e?.response?.data?.detail || 'Erreur chargement stats')
      } finally {
        setLoading(false)
      }
    })()
  }, [])

  const totalCourses = useMemo(() => Object.keys(stats?.by_course || {}).length, [stats])
  const totalStatuses = useMemo(() => Object.keys(stats?.by_status || {}).length, [stats])
  const activityTotal = useMemo(() => (stats?.activity_7d || []).reduce((acc, item) => acc + item.count, 0), [stats])

  if (error) {
    return (
      <div className="rounded-3xl border border-red-200 bg-red-50 p-6 text-red-700 dark:border-red-900/60 dark:bg-red-950/40 dark:text-red-200">
        {error}
      </div>
    )
  }

  return (
    <div className="relative overflow-hidden">
      <div
        aria-hidden
        className="absolute inset-0 -z-20 bg-gradient-to-b from-emerald-50 via-white to-white dark:from-zinc-950 dark:via-zinc-900 dark:to-zinc-900"
      />
      <div aria-hidden className="absolute inset-x-0 top-0 -z-30 h-[420px] bg-[radial-gradient(circle_at_top,_rgba(16,185,129,0.25),_transparent_65%)]" />
      <div aria-hidden className="absolute inset-0 -z-10 bg-[radial-gradient(circle_at_20%_20%,rgba(236,72,153,0.15),transparent_60%),radial-gradient(circle_at_80%_10%,rgba(168,85,247,0.18),transparent_55%)] dark:bg-[radial-gradient(circle_at_15%_20%,rgba(236,72,153,0.12),transparent_55%),radial-gradient(circle_at_85%_15%,rgba(168,85,247,0.15),transparent_50%)]" />

      <div className="relative mx-auto flex max-w-[1600px] flex-col gap-6 px-6 py-6">

        {loading && (
          <section className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {Array.from({ length: 4 }).map((_, index) => (
              <div
                key={index}
                className="h-32 animate-pulse rounded-3xl border border-emerald-100/70 bg-white/60 dark:border-emerald-900/40 dark:bg-zinc-900/60"
              />
            ))}
          </section>
        )}

        {stats && (
          <>
            <section className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              <StatCard
                title="Médias indexés"
                value={stats.totals.media}
                description="Nombre total de fichiers traités"
              />
              <StatCard
                title="Embeddings"
                value={stats.totals.embeddings}
                description="Vecteurs disponibles pour le RAG"
              />
              <StatCard
                title="Statuts suivis"
                value={totalStatuses}
                description="États de traitement actifs"
              />
              <StatCard
                title="Formations reliées"
                value={totalCourses}
                description="Cours ou parcours connectés"
              />
            </section>

            <section className="grid gap-6 lg:grid-cols-[1.1fr_0.9fr]">
              <div className="space-y-6 rounded-3xl border border-emerald-100/80 bg-white/90 p-6 shadow-xl shadow-emerald-500/10 dark:border-emerald-900/50 dark:bg-zinc-950/90">
                <div className="flex flex-wrap items-center justify-between gap-4">
                  <div>
                    <h2 className="text-xl font-semibold text-zinc-900 dark:text-white">Derniers médias ingérés</h2>
                    <p className="text-sm text-zinc-500 dark:text-zinc-400">
                      Trois derniers contenus traités. Suivez leur statut et la formation associée.
                    </p>
                  </div>
                </div>
                <div className="overflow-hidden rounded-2xl border border-zinc-100/80 bg-white/95 text-sm shadow-sm dark:border-zinc-800 dark:bg-zinc-900/90">
                  <table className="w-full border-collapse">
                    <thead className="bg-emerald-50/50 border-b border-emerald-100/70 dark:bg-zinc-900/50 dark:border-emerald-900/40">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-emerald-600 dark:text-emerald-400 uppercase tracking-wider">Titre</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-emerald-600 dark:text-emerald-400 uppercase tracking-wider">Statut</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-emerald-600 dark:text-emerald-400 uppercase tracking-wider">Formation</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-emerald-600 dark:text-emerald-400 uppercase tracking-wider">Date</th>
                      </tr>
                    </thead>
                    <tbody>
                      {stats.recent?.length ? (
                        stats.recent.slice(0, 6).map((item) => (
                          <tr key={item.id} className="border-t border-zinc-100/80 dark:border-zinc-800/80">
                            <td className="px-4 py-3 font-medium text-zinc-800 dark:text-zinc-100">{item.title}</td>
                            <td className="px-4 py-3">
                              <span
                                className={`inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs font-semibold ${statusPalette[item.status] || 'bg-zinc-100 text-zinc-600 dark:bg-zinc-800/80 dark:text-zinc-300'}`}
                              >
                                <span className="inline-block h-2 w-2 rounded-full bg-current" aria-hidden />
                                {formatStatus(item.status)}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-zinc-600 dark:text-zinc-300">{item.course_title ?? item.course_id ?? '—'}</td>
                            <td className="px-4 py-3 text-zinc-500 dark:text-zinc-400">{formatDate(item.created_at)}</td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan={4} className="px-4 py-6 text-center text-sm text-zinc-500 dark:text-zinc-400">
                            Aucun média disponible pour le moment.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              <aside className="space-y-6 rounded-3xl border border-zinc-200/70 bg-white/95 p-6 shadow-lg dark:border-zinc-800 dark:bg-zinc-900/90">
                <section className="space-y-3">
                  <h3 className="text-sm font-semibold uppercase tracking-wide text-emerald-600 dark:text-emerald-300">
                    Activité 7 derniers jours
                  </h3>
                  <div className="text-3xl font-bold text-zinc-900 dark:text-white">{activityTotal}</div>
                  <p className="text-sm text-zinc-500 dark:text-zinc-400">
                    Total des médias traités sur les sept derniers jours.
                  </p>
                  <div className="space-y-2">
                    {(stats.activity_7d || []).map((entry) => (
                      <div key={entry.day} className="flex items-center justify-between text-sm text-zinc-600 dark:text-zinc-300">
                        <span>{formatDay(entry.day)}</span>
                        <span className="font-semibold text-zinc-900 dark:text-white">{entry.count}</span>
                      </div>
                    ))}
                  </div>
                </section>

                <section className="space-y-3">
                  <h3 className="text-sm font-semibold uppercase tracking-wide text-emerald-600 dark:text-emerald-300">
                    Statuts actifs
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {Object.entries(stats.by_status).map(([status, value]) => (
                      <span
                        key={status}
                        className={`inline-flex items-center gap-2 rounded-full border border-emerald-200/70 bg-emerald-50/60 px-3 py-1 text-xs font-semibold text-emerald-700 dark:border-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-200`}
                      >
                        {formatStatus(status)}
                        <span className="rounded-full bg-emerald-500/20 px-2 py-0.5 text-[11px] font-bold text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-200">
                          {value}
                        </span>
                      </span>
                    ))}
                    {!Object.keys(stats.by_status || {}).length && (
                      <span className="text-sm text-zinc-500 dark:text-zinc-400">Aucun statut suivi.</span>
                    )}
                  </div>
                </section>
              </aside>
            </section>
          </>
        )}
      </div>
    </div>
  )
}

function StatCard({ title, value, description }: { title: string; value: number; description: string }) {
  return (
    <div className="relative overflow-hidden rounded-3xl border border-emerald-100/70 bg-white/90 p-6 shadow-lg shadow-emerald-500/10 transition hover:-translate-y-0.5 hover:shadow-emerald-500/20 dark:border-emerald-900/50 dark:bg-zinc-950/90">
      <div aria-hidden className="absolute -right-12 -top-12 h-32 w-32 rounded-full bg-emerald-400/20 blur-3xl dark:bg-emerald-500/30" />
      <div className="relative space-y-2">
        <p className="text-xs font-semibold uppercase tracking-[0.25em] text-emerald-600 dark:text-emerald-300">{title}</p>
        <div className="text-3xl font-bold text-zinc-900 dark:text-white">{value.toLocaleString('fr-FR')}</div>
        <p className="text-sm text-zinc-500 dark:text-zinc-400">{description}</p>
      </div>
    </div>
  )
}

function formatStatus(status: string) {
  if (!status) return '—'
  return status
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (letter) => letter.toUpperCase())
}

function formatDate(date: string) {
  return new Date(date).toLocaleString('fr-FR', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

function formatDay(day: string) {
  const parsed = new Date(day)
  if (Number.isNaN(parsed.getTime())) return day
  return parsed.toLocaleDateString('fr-FR', { weekday: 'short', day: '2-digit' })
}
